import React, { useEffect, useMemo, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { snakeCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Avatar,
  Button,
  Box as Container,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Pagination,
  RowsPerPageType,
  RowsSkeletonLoader,
  useSidePanel,
  Box as Wrapper,
} from '@node-space/ui-components'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationNextPageItemCount } from 'hooks/usePaginationNextPageItemCount'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { ContactInstrumentsSheet } from 'pages/Contacts/ContactInstruments'
import { ContactActionIds, ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { Contact } from 'types/contacts'
import { PaginationPaging } from 'types/pagination'
import { getBeneficiaryName, getBeneficiaryType, getEntityType } from 'utils/beneficiaries'
import { BeneficiaryEmptyState } from './BeneficiaryEmptyState'
import { BeneficiaryErrorState } from './BeneficiaryErrorState'
import { BeneficiarySummary } from './BeneficiarySummary'

interface BeneficiaryTableProps {
  setBeneficiaryToDelete: React.Dispatch<React.SetStateAction<BeneficiaryDetails>>
  setIsAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleEditBeneficiary: (beneficiary: BeneficiaryDetails) => void
  beneficiaryList?: BeneficiaryDetails[]
  pagination?: PaginationPaging
  isPending: boolean
  isError: boolean
  page: number
  rowsPerPage: RowsPerPageType
  handleChangePage: (newPage: number) => void
  handleChangeRowsPerPage: (newRowsPerPage: RowsPerPageType) => void
}

export const BeneficiaryTable = ({
  setBeneficiaryToDelete,
  setIsAddModalOpen,
  handleEditBeneficiary,
  beneficiaryList,
  pagination,
  isPending,
  isError,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
}: BeneficiaryTableProps) => {
  const { t } = useTranslation()
  const { enableBeneficiaryToContactMigration } = useFeatureFlags()
  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [isContactInstrumentSheetOpen, setIsContactInstrumentSheetOpen] = useState(false)
  const setContact = useSetAtom(contactAtom)

  const {
    isSidePanelOpen: isBeneficiaryDetailsOpen,
    sidePanelData: beneficiaryDetails,
    paging: pageBeneficiaries,
    setIsSidePanelOpen: setIsBeneficiaryDetailsOpen,
    updateSidePanel: updateBeneficiaryDetails,
  } = useSidePanel<BeneficiaryDetails>(beneficiaryList)

  const { paging, showPagination } = usePaginationPagingPersist(pagination)
  const permissions = useMappedRoles().manageBeneficiaries

  const canEdit = permissions?.canEdit
  const { meta } = contactAction || {}
  const { contactBeneficiary } = meta || {}

  useEffect(() => {
    if (
      contactInstrumentAction?.actionId === ContactInstrumentActionIds.FIAT_INSTRUMENT &&
      contactBeneficiary
    ) {
      setIsContactInstrumentSheetOpen(true)
    } else if (!contactInstrumentAction) {
      setIsContactInstrumentSheetOpen(false)
    }
  }, [contactInstrumentAction, contactBeneficiary])

  const columns = useMemo(() => {
    return [
      { key: 'accountHolder', label: t('accountHolder'), variant: DataTableCellVariants.TEXT },
      { key: 'currency', label: t('currency'), variant: DataTableCellVariants.TEXT },
      {
        key: 'beneficiaryType',
        label: t('manageBeneficiaries.beneficiaryType'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'entityType',
        label: t('manageBeneficiaries.entityType'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
        align: DataTableColumnAlignments.RIGHT,
      },
    ]
  }, [])

  const onOpenBeneficiaryDetails = (beneficiaryIndex: number) => {
    updateBeneficiaryDetails(beneficiaryIndex)
    setIsBeneficiaryDetailsOpen(true)
  }

  const handleContactSelect = (contact: Contact) => {
    setContact(contact)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      actionId: ContactInstrumentActionIds?.FIAT_INSTRUMENT,
      header: t('contacts.addBankAccount'),
      onClose: () => {
        setIsSheetOpen(false)
        setIsContactInstrumentSheetOpen(false)
        setContactInstrumentAction(null)
        setContact(null)
        setContactAction({
          ...contactAction,
          meta: {
            ...contactAction?.meta,
            contactBeneficiary: null,
          },
        })
      },
    })
    setIsContactInstrumentSheetOpen(true)
  }

  const handleAssignContactClick = (beneficiary: BeneficiaryDetails) => {
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT_ASSIGN,
      header: t('contacts.assignContact'),
      onClose: () => {
        setIsSheetOpen(false)
        setContact(null)
        setContactAction({
          ...contactAction,
          meta: {
            ...contactAction?.meta,
            contactBeneficiary: null,
          },
        })
      },
      meta: {
        contactBeneficiary: beneficiary,
        handleContactSelect,
      },
    })
    setIsSheetOpen(true)
  }

  const tableData = useMemo((): DataTableRowData[] => {
    return beneficiaryList?.map((beneficiary: BeneficiaryDetails, index: number) => {
      return {
        id: beneficiary?.reference,
        testid: beneficiary?.reference,
        onRowClick: () => {
          onOpenBeneficiaryDetails(index)
        },
        cells: [
          {
            key: 'accountHolder',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-title`,
            variant: 'TEXT',
            Text: `${getBeneficiaryName(beneficiary)} ${beneficiary?.alias ? ` (${beneficiary?.alias})` : ''}`,
            BottomText: beneficiary?.fiat?.accountNumber,
          },
          {
            key: 'currency',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-currency`,
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar type="currency" name={beneficiary?.currency} />,
            Text: beneficiary?.currency?.replace('Fdusd', 'FDUSD'),
          },
          {
            key: 'beneficiaryType',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-beneficiary-type`,
            variant: DataTableCellVariants.TEXT,
            Text: getBeneficiaryType(beneficiary?.beneficiaryType, t),
          },
          {
            key: 'entityType',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-entity-type`,
            variant: DataTableCellVariants.TEXT,
            Text: getEntityType(beneficiary?.entityType, t),
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: canEdit
              ? [
                  ...(enableBeneficiaryToContactMigration
                    ? [
                        <Button
                          key="assign-contact"
                          iconElement={<Icon name="UserCirclePlus" />}
                          variant="ghost"
                          onClick={e => {
                            e.stopPropagation()
                            handleAssignContactClick(beneficiary)
                          }}
                        >
                          {t('contacts.assignContact')}
                        </Button>,
                      ]
                    : []),
                  <DropdownMenu
                    key={`context-menu-${beneficiary?.reference}`}
                    DropdownMenuItems={[
                      <DropdownMenuItem
                        key="details"
                        LeadingIcon={<Icon name="Info" />}
                        onClick={() => onOpenBeneficiaryDetails(index)}
                      >
                        {t('details')}
                      </DropdownMenuItem>,
                      <DropdownMenuItem
                        key="delete"
                        LeadingIcon={<Icon name="Trash" />}
                        onClick={() => setBeneficiaryToDelete(beneficiary)}
                      >
                        {t('delete')}
                      </DropdownMenuItem>,
                      <DropdownMenuItem
                        key="edit"
                        LeadingIcon={<Icon name="SimplePencilCustom" />}
                        onClick={() => handleEditBeneficiary(beneficiary)}
                      >
                        {t('manageBeneficiaries.editBeneficiary')}
                      </DropdownMenuItem>,
                    ]}
                  >
                    <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
                  </DropdownMenu>,
                ]
              : [],
          },
        ],
      }
    })
  }, [beneficiaryList])

  const nextPageRowCount = usePaginationNextPageItemCount(paging)

  return (
    <>
      <Wrapper tag="span" name="container-beneficiary">
        {isError ? (
          <BeneficiaryErrorState onRefresh={() => window.location.reload()} />
        ) : (
          <>
            <Container
              tag="span"
              className={`${isPending && 'border-primaryTint'} rounded middle grid`}
            >
              {isPending ? (
                <RowsSkeletonLoader rows={nextPageRowCount} />
              ) : (
                <>
                  {beneficiaryList?.length ? (
                    <>
                      <DataTable headers={columns} data={tableData} />
                      <BeneficiarySummary
                        isOpen={isBeneficiaryDetailsOpen}
                        beneficiaryDetails={beneficiaryDetails}
                        pageBeneficiaries={pageBeneficiaries}
                        onClose={() => setIsBeneficiaryDetailsOpen(false)}
                      />
                    </>
                  ) : (
                    <BeneficiaryEmptyState onAddBeneficiary={() => setIsAddModalOpen(true)} />
                  )}
                </>
              )}
            </Container>

            {!!paging && !showPagination && (
              <Container className={`${isPending || showPagination ? 'opacity-25' : ''}`}>
                <Pagination
                  testId="beneficiary-pagination"
                  page={page}
                  rowsPerPage={rowsPerPage}
                  total={paging?.total ?? 0}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </Container>
            )}
          </>
        )}
      </Wrapper>
      <ContactsSheets isSheetOpen={isSheetOpen} />
      <ContactInstrumentsSheet isSheetOpen={isContactInstrumentSheetOpen} />
    </>
  )
}

export default BeneficiaryTable
